import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { ChatIcon, HeartIcon, TrashIcon } from '@heroicons/react/outline'
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/solid'
import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import SocialReactions from './SocialReactions'
import SocialReplies from './SocialReplies'

const SocialPost = ({ post, onPostUpdate, onPostDelete, showFullReplies = false, initialReplies = [], onReplyCreated }) => {
  const [showReplies, setShowReplies] = useState(showFullReplies)
  const [isDeleting, setIsDeleting] = useState(false)
  const [currentUser] = useGlobalState('currentUser')
  const { putpostRequest } = useQuery()

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this post?')) return
    
    setIsDeleting(true)

    putpostRequest(`/api/v3/social_posts/${post.id}`, 'DELETE', { }, (err, jsonData) => {
      setIsDeleting(false)
      if (err) return
      if (onPostDelete) onPostDelete(post.id)
    })
  }

  const handleReactionUpdate = (updatedPost) => {
    if (onPostUpdate) onPostUpdate(updatedPost)
  }

  const timeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    
    if (diffInSeconds < 60) return `${diffInSeconds}s`
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`
    return `${Math.floor(diffInSeconds / 86400)}d`
  }

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
      <div className="flex space-x-3">
        <div className="flex-shrink-0">
          <img
            className="h-10 w-10 rounded-full"
            src={post.user.image_url}
            alt={post.user.display_name}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="font-semibold text-gray-900 dark:text-white">
                {post.user.display_name}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                @{post.user.username}
              </span>
              <span className="text-gray-500 dark:text-gray-400">·</span>
              <a href={`/social?post_id=${post.id}`} target="_blank" className="text-gray-500 dark:text-gray-400 text-sm">
                {timeAgo(post.created_at)}
              </a>
            </div>
            {currentUser?.id === post.user.id && (
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="text-gray-400 hover:text-red-500 p-1 rounded disabled:opacity-50"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          
          <div 
            className={`mt-2`}
          >
            <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
              {post.content}
            </p>
          </div>

          <div className="mt-3 flex items-center space-x-6">
            <button
              onClick={() => !showFullReplies && setShowReplies(!showReplies)}
              className={`flex items-center space-x-2 text-gray-500 transition-colors ${!showFullReplies ? 'hover:text-cccblue cursor-pointer' : 'cursor-default'}`}
            >
              <ChatIcon className="h-5 w-5" />
              <span className="text-sm">{post.replies_count}</span>
            </button>

            <SocialReactions 
              post={post} 
              onReactionUpdate={handleReactionUpdate}
            />
          </div>

          {showReplies && (
            <div className="mt-4">
              <SocialReplies 
                postId={post.id} 
                initialReplies={showFullReplies ? initialReplies : []}
                onReplyCountChange={(newCount) => {
                  const updatedPost = { ...post, replies_count: newCount }
                  if (onPostUpdate) onPostUpdate(updatedPost)
                }}
                onReplyCreated={onReplyCreated}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

SocialPost.propTypes = {
  post: PropTypes.object.isRequired,
  onPostUpdate: PropTypes.func,
  onPostDelete: PropTypes.func,
  showFullReplies: PropTypes.bool,
  initialReplies: PropTypes.array,
  onReplyCreated: PropTypes.func
}

export default SocialPost
